import { useState } from "react";
import { X, PhoneCall, Check, AlertCircle, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Modal that lets unauthenticated users (on login / register pages) request
 * a callback from customer support.  Submits to /api/public/callbacks/request.
 *
 * Props:
 *   role     - "patient" | "doctor"  (pre-selected, shown but locked)
 *   onClose  - close handler
 */
export default function CallbackRequestModal({ role = "patient", onClose }) {
  const [form, setForm] = useState({ name: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim() || form.name.trim().length < 2) { setErr("Please enter your name."); return; }
    if (!form.phone.trim() || form.phone.trim().length < 5) { setErr("Please enter a valid phone number."); return; }
    setBusy(true);
    try {
      await api.post("/public/callbacks/request", {
        name: form.name.trim(), phone: form.phone.trim(), role,
      });
      setDone(true);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Could not submit. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="cbreq-overlay" onClick={onClose} data-testid="callback-modal-overlay">
      <div className="cbreq-modal" onClick={(e) => e.stopPropagation()} data-testid="callback-modal">
        <header>
          <b><PhoneCall size={16} /> Request a callback</b>
          <button onClick={onClose} className="cbreq-x" data-testid="callback-modal-close" aria-label="Close"><X size={18} /></button>
        </header>

        {done ? (
          <div className="cbreq-success" data-testid="callback-success">
            <Check size={28} />
            <h3>Got it — we&apos;ll call you back.</h3>
            <p>Our customer support team will reach out to <b>{form.phone}</b> within a few hours during working hours (Mon–Sat, 9 AM – 7 PM IST).</p>
            <button className="pg-btn pg-btn-ink" onClick={onClose} data-testid="callback-success-close">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="cbreq-form">
            <p className="cbreq-lead">
              Trouble signing in or registering? Leave your number and our customer
              support team will call you back to help.
            </p>
            <label>Your name *
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Full name"
                autoFocus
                data-testid="callback-input-name"
              />
            </label>
            <label>Phone number *
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+91 98000 00000"
                data-testid="callback-input-phone"
              />
            </label>
            <label>I am a
              <select value={role} disabled data-testid="callback-input-role">
                <option value="patient">Patient</option>
                <option value="doctor">Doctor</option>
              </select>
            </label>
            {err && <p className="cbreq-err" data-testid="callback-error"><AlertCircle size={13} /> {err}</p>}
            <button className="pg-btn pg-btn-ink full" type="submit" disabled={busy} data-testid="callback-submit">
              {busy ? <><Loader2 size={14} className="spin" /> Submitting…</> : "Request callback"}
            </button>
            <p className="cbreq-foot">Free of cost · Available Mon–Sat, 9 AM – 7 PM IST</p>
          </form>
        )}
      </div>
    </div>
  );
}
